/**
 * milestoneChord — a tiny Web Audio API "cha-ching" that fires alongside
 * the tier-milestone pulse. Synthesized (not a file) so it ships as zero
 * bytes of audio.
 *
 * Feb 24 2026 (Karine ask): "one-time gentle mini-chord — indulgently
 * luxe, reinforces the moment. Muted by default until the client opts
 * in, of course."
 *
 *   • Two-note luxe chord: G4 → C5 (bright major fifth, warm bell tail)
 *   • Total run time ~ 900 ms
 *   • Very low peak gain (0.14) so it's confident but never startling
 *   • Fails silent if Web Audio isn't available (older iOS, autoplay
 *     policy without a prior user gesture, private-browsing edge cases).
 *
 * The client opts in via a bell toggle inside the tier-progress bubble;
 * preference persists in localStorage["kl_milestone_sound"].
 */

const PREF_KEY = "kl_milestone_sound"; // "on" | "off" (default off)

export function isMilestoneSoundEnabled() {
  try { return localStorage.getItem(PREF_KEY) === "on"; }
  catch { return false; }
}

export function setMilestoneSoundEnabled(on) {
  try { localStorage.setItem(PREF_KEY, on ? "on" : "off"); }
  catch { /* silent */ }
}

// Reuse a single AudioContext across firings — modern browsers cap the
// number you can create and re-instantiating on every play adds latency.
let _ctx = null;
function ensureContext() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_ctx) {
    try { _ctx = new AC(); } catch { return null; }
  }
  // If the tab was backgrounded and Chrome suspended the context, resume.
  if (_ctx.state === "suspended") {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

/**
 * Play the celebratory two-note chord.
 * Returns `true` if a sound was fired, `false` if suppressed (opt-out,
 * unsupported browser, or scheduling failure).
 */
export function playMilestoneChord({ forcePlay = false } = {}) {
  if (!forcePlay && !isMilestoneSoundEnabled()) return false;
  const ctx = ensureContext();
  if (!ctx) return false;

  try {
    const now = ctx.currentTime;
    // Master bus — one gentle attack, a long soft tail.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.14, now + 0.04);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);
    master.connect(ctx.destination);

    // Two-note voicing: G4 (392Hz) triangle warmth + C5 (523.25Hz)
    // sine sparkle. Both feed the same master so they duck together.
    const voice = (freq, delay, type) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, now + delay);
      g.gain.exponentialRampToValueAtTime(0.9, now + delay + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.75);
      osc.connect(g);
      g.connect(master);
      osc.start(now + delay);
      osc.stop(now + delay + 0.85);
    };
    voice(392.00, 0.00, "triangle"); // G4
    voice(523.25, 0.09, "sine");     // C5 (stagger for the "cha-ching")
    return true;
  } catch {
    return false;
  }
}
